import { useEffect, useState, type ImgHTMLAttributes } from 'react';
import { fileService } from '../../services/fileService';

type Props = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> & {
  /** Stored canonical S3 URL, object key, or already-signed URL. */
  mediaSrc?: string | null;
};

type LoadState = 'loading' | 'ready' | 'error';

/**
 * Renders an image that may live in a private S3 bucket: resolves to a presigned GET URL when needed.
 */
export function ResolvedImage({ mediaSrc, className, alt, onError, ...imgProps }: Props) {
  const [resolved, setResolved] = useState<string | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('loading');

  useEffect(() => {
    let cancelled = false;
    if (!mediaSrc?.trim()) {
      setResolved(null);
      setLoadState('loading');
      return;
    }
    setResolved(null);
    setLoadState('loading');
    fileService
      .resolveMediaUrl(mediaSrc)
      .then((url) => {
        if (!cancelled) {
          setResolved(url);
          setLoadState('ready');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResolved(null);
          setLoadState('error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [mediaSrc]);

  if (!mediaSrc?.trim()) return null;

  if (loadState === 'error' || (loadState === 'ready' && !resolved)) {
    return (
      <span
        className={`inline-flex items-center justify-center bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500 ${className ?? ''}`}
        role="img"
        aria-label={alt ? `${alt} (failed to load)` : 'Image failed to load'}
        title="Could not load image"
      >
        <svg className="h-8 w-8 shrink-0 opacity-60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <path d="M21 15l-5-5L5 21" />
        </svg>
      </span>
    );
  }

  if (loadState === 'loading' || !resolved) {
    return (
      <span
        className={`inline-block bg-gray-200 animate-pulse dark:bg-gray-700 ${className ?? ''}`}
        aria-hidden
      />
    );
  }

  return (
    <img
      {...imgProps}
      src={resolved}
      alt={alt ?? ''}
      className={className}
      onError={(e) => {
        setLoadState('error');
        onError?.(e);
      }}
    />
  );
}
